import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Save, Loader2, UploadCloud, Video, Image as ImageIcon,
  Eye, EyeOff, ExternalLink, ChevronUp, ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { invalidateSettingsCache } from '../../lib/settingsCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_STREAMS = 3;

const blankStream = () => ({ name: '', url: '' });
const blankLocation = () => ({
  name: '',
  description: '',
  logo_url: '',
  cover_image_url: '',
  sort_order: 0,
  is_active: true,
  streams: [blankStream()],
});

/** Drag & drop (or click) image uploader used for the cover and the logo. */
const ImageDrop = ({ label, hint, value, onUploaded, folder, aspect, testId }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please drop an image file.', variant: 'destructive' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: 'Too large', description: 'Images must be 20MB or smaller.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      const { data } = await axios.post(`${API}/storage/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (!data?.url) throw new Error('Upload response missing URL');
      onUploaded(data.url);
      toast({ title: 'Uploaded', description: `${label} ready. Remember to save.` });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Could not upload the image.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          upload(e.dataTransfer.files?.[0]);
        }}
        onClick={() => inputRef.current?.click()}
        className={`mt-1 relative ${aspect} w-full rounded-xl border-2 border-dashed cursor-pointer overflow-hidden flex items-center justify-center transition-colors ${
          dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400'
        }`}
        data-testid={testId}
      >
        {value ? (
          <img src={resolveMediaUrl(value)} alt={label} className="w-full h-full object-contain" />
        ) : (
          <div className="text-center px-3 py-6 text-gray-500">
            {uploading ? (
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-blue-600" />
            ) : (
              <>
                <UploadCloud className="w-7 h-7 mx-auto mb-2 text-gray-400" />
                <p className="text-sm font-medium">Drag &amp; drop or click</p>
                <p className="text-xs mt-1">{hint}</p>
              </>
            )}
          </div>
        )}
        {uploading && value && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        )}
      </div>
      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {value && (
        <button
          type="button"
          onClick={() => onUploaded('')}
          className="mt-2 text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1"
        >
          <Trash2 className="w-4 h-4" /> Remove
        </button>
      )}
    </div>
  );
};

const LocationEditor = ({ location, onChange, onSave, onDelete, saving, isOpen, onToggle }) => {
  const streams = location.streams?.length ? location.streams : [blankStream()];

  const setStream = (index, patch) => {
    const next = streams.map((s, i) => (i === index ? { ...s, ...patch } : s));
    onChange({ ...location, streams: next });
  };

  return (
    <Card data-testid={`webcam-location-editor-${location.id || 'new'}`}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onToggle}
              className="p-1 rounded text-slate-500 hover:text-blue-700 hover:bg-slate-100 transition-colors"
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Collapse location' : 'Expand location'}
              data-testid={`webcam-accordion-${location.id || 'new'}`}
            >
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <Video className="w-5 h-5 text-blue-600" />
            <button
              type="button"
              onClick={onToggle}
              className="text-left"
              data-testid={`webcam-accordion-title-${location.id || 'new'}`}
            >
              <CardTitle className="text-base">{location.name || 'New Location'}</CardTitle>
            </button>
            {location.is_active ? (
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> Visible
              </span>
            ) : (
              <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <EyeOff className="w-3 h-3" /> Hidden
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange({ ...location, is_active: !location.is_active })}
              data-testid={`webcam-toggle-active-${location.id || 'new'}`}
            >
              {location.is_active ? 'Hide' : 'Show'}
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving} data-testid={`webcam-save-${location.id || 'new'}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            {location.id && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700"
                onClick={onDelete}
                data-testid={`webcam-delete-${location.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <CardDescription>
          {(location.streams || []).filter((st) => (st.url || '').trim()).length} of {MAX_STREAMS} streams
          &middot; order {location.sort_order ?? 0}
          {!isOpen && ' \u2014 click the chevron to edit'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5" hidden={!isOpen}>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Location Name</Label>
            <Input
              value={location.name}
              onChange={(e) => onChange({ ...location, name: e.target.value })}
              placeholder="e.g. THE BEACH BAR ST. JOHN"
              className="mt-1"
              data-testid={`webcam-name-${location.id || 'new'}`}
            />
          </div>
          <div>
            <Label>Order Number</Label>
            <Input
              type="number"
              value={location.sort_order}
              onChange={(e) => onChange({ ...location, sort_order: Number(e.target.value) || 0 })}
              className="mt-1"
              data-testid={`webcam-order-${location.id || 'new'}`}
            />
          </div>
        </div>

        <div>
          <Label>Short Description (optional)</Label>
          <Input
            value={location.description}
            onChange={(e) => onChange({ ...location, description: e.target.value })}
            placeholder="Shown under the location name"
            className="mt-1"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <ImageDrop
            label="Cover Image"
            hint="16:9 works best"
            value={location.cover_image_url}
            folder="webcams/covers"
            aspect="aspect-video"
            testId={`webcam-cover-drop-${location.id || 'new'}`}
            onUploaded={(url) => onChange({ ...location, cover_image_url: url })}
          />
          <ImageDrop
            label="Location Logo"
            hint="transparent PNG preferred"
            value={location.logo_url}
            folder="webcams/logos"
            aspect="aspect-video"
            testId={`webcam-logo-drop-${location.id || 'new'}`}
            onUploaded={(url) => onChange({ ...location, logo_url: url })}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-blue-600" /> Streams ({streams.length}/{MAX_STREAMS})
            </Label>
            {streams.length < MAX_STREAMS && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onChange({ ...location, streams: [...streams, blankStream()] })}
                data-testid={`webcam-add-stream-${location.id || 'new'}`}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Stream
              </Button>
            )}
          </div>

          <div className="space-y-3">
            {streams.map((stream, index) => (
              <div key={index} className="grid md:grid-cols-[1fr_2fr_auto] gap-3 items-end bg-gray-50 p-3 rounded-lg">
                <div>
                  <Label className="text-xs">Short Name</Label>
                  <Input
                    value={stream.name}
                    onChange={(e) => setStream(index, { name: e.target.value })}
                    placeholder="View Live Cam"
                    className="mt-1"
                    data-testid={`webcam-stream-name-${location.id || 'new'}-${index + 1}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">YouTube Stream URL</Label>
                  <Input
                    value={stream.url}
                    onChange={(e) => setStream(index, { url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="mt-1"
                    data-testid={`webcam-stream-url-${location.id || 'new'}-${index + 1}`}
                  />
                </div>
                <div className="flex items-center gap-1 pb-1">
                  {stream.url && (
                    <a
                      href={stream.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-500 hover:text-blue-700"
                      title="Open in YouTube"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {streams.length > 1 && (
                    <button
                      type="button"
                      onClick={() => onChange({ ...location, streams: streams.filter((_, i) => i !== index) })}
                      className="p-2 text-red-600 hover:text-red-700"
                      title="Remove stream"
                      data-testid={`webcam-remove-stream-${location.id || 'new'}-${index + 1}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminWebcamLocations = () => {
  const [locations, setLocations] = useState([]);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  // every existing location starts collapsed to keep this page short
  const [openId, setOpenId] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/webcams/locations`, { headers: authHeaders() });
      setLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: 'Load failed',
        description: error.response?.data?.detail || 'Could not load webcam locations.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const bustPublicCache = () =>
    invalidateSettingsCache(`${process.env.REACT_APP_BACKEND_URL}/api/public/webcams/locations`);

  const saveLocation = async (location) => {
    if (!location.name?.trim()) {
      toast({ title: 'Name required', description: 'Give the location a name first.', variant: 'destructive' });
      return;
    }
    setSavingId(location.id || 'new');
    try {
      const payload = {
        name: location.name,
        description: location.description || '',
        logo_url: location.logo_url || '',
        cover_image_url: location.cover_image_url || '',
        sort_order: Number(location.sort_order) || 0,
        is_active: Boolean(location.is_active),
        streams: (location.streams || []).filter((s) => (s.url || '').trim()),
      };
      if (location.id) {
        await axios.put(`${API}/webcams/locations/${location.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/webcams/locations`, payload, { headers: authHeaders() });
        setDraft(null);
      }
      bustPublicCache();
      toast({ title: 'Saved', description: `${payload.name} updated.` });
      await load();
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save the location.',
        variant: 'destructive',
      });
    } finally {
      setSavingId(null);
    }
  };

  const deleteLocation = async (location) => {
    if (!window.confirm(`Delete "${location.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/webcams/locations/${location.id}`, { headers: authHeaders() });
      bustPublicCache();
      toast({ title: 'Deleted', description: `${location.name} removed.` });
      await load();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Could not delete the location.',
        variant: 'destructive',
      });
    }
  };

  const nudge = async (location, direction) => {
    const next = (Number(location.sort_order) || 0) + direction;
    setLocations((prev) => prev.map((l) => (l.id === location.id ? { ...l, sort_order: next } : l)));
    try {
      await axios.put(
        `${API}/webcams/locations/${location.id}`,
        { sort_order: next },
        { headers: authHeaders() },
      );
      bustPublicCache();
      await load();
    } catch (error) {
      toast({ title: 'Reorder failed', description: 'Could not change the order.', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="webcams-admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl" data-testid="admin-webcam-locations">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Video className="w-7 h-7 text-blue-600" />
            Live Webcams
          </h2>
          <p className="text-gray-500 mt-1">
            {locations.length} location{locations.length === 1 ? '' : 's'} &middot; up to {MAX_STREAMS} streams each.
            Turn the whole component on or off with the <strong>webcams_enabled</strong> feature flag.
          </p>
        </div>
        {!draft && (
          <Button onClick={() => { setDraft(blankLocation()); setOpenId('new'); }} data-testid="webcam-add-location">
            <Plus className="w-4 h-4 mr-2" /> Add Location
          </Button>
        )}
      </div>

      <div className="space-y-6">
        {draft && (
          <LocationEditor
            location={draft}
            onChange={setDraft}
            onSave={() => saveLocation(draft)}
            onDelete={() => setDraft(null)}
            saving={savingId === 'new'}
            isOpen={openId === 'new'}
            onToggle={() => setOpenId(openId === 'new' ? null : 'new')}
          />
        )}

        {locations.map((location) => (
          <div key={location.id} className="relative">
            <div className="absolute -left-10 top-6 hidden lg:flex flex-col gap-1">
              <button
                onClick={() => nudge(location, -1)}
                className="p-1 rounded bg-white border border-gray-200 text-gray-600 hover:text-blue-700"
                title="Move up"
                data-testid={`webcam-move-up-${location.id}`}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => nudge(location, 1)}
                className="p-1 rounded bg-white border border-gray-200 text-gray-600 hover:text-blue-700"
                title="Move down"
                data-testid={`webcam-move-down-${location.id}`}
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <LocationEditor
              location={location}
              onChange={(next) => setLocations((prev) => prev.map((l) => (l.id === next.id ? next : l)))}
              onSave={() => saveLocation(location)}
              onDelete={() => deleteLocation(location)}
              saving={savingId === location.id}
              isOpen={openId === location.id}
              onToggle={() => setOpenId(openId === location.id ? null : location.id)}
            />
          </div>
        ))}

        {!draft && locations.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              No webcam locations yet. Click <strong>Add Location</strong> to create the first one.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminWebcamLocations;
